import type React from "react";
import { Box, Card, Stack, Typography, useTheme } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { tokens } from "../../../../theme";
import type { ApplicationSubmission, JobPosition } from "../../types";

interface MetricsCardsProps {
  submissions: ApplicationSubmission[];
  jobs: JobPosition[];
}

const MetricsCards: React.FC<MetricsCardsProps> = ({ submissions, jobs }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const totalApplicants = submissions.length;
  const avgScore = totalApplicants
    ? Math.round(submissions.reduce((acc, s) => acc + s.overallScore, 0) / totalApplicants)
    : 0;
  const highScorersCount = submissions.filter((s) => s.overallScore >= 80).length;
  const activeJobsCount = jobs.filter((j) => j.active).length;

  const cards = [
    { label: "Total Applicants", value: totalApplicants, icon: GroupsOutlinedIcon, color: "rgba(0,122,255,1)" },
    { label: "Average Score", value: `${avgScore}%`, icon: TrendingUpIcon, color: "rgba(52,199,89,1)" },
    { label: "High Scorers (80+)", value: highScorersCount, icon: EmojiEventsOutlinedIcon, color: "rgba(255,149,0,1)" },
    { label: "Active Positions", value: activeJobsCount, icon: WorkOutlineIcon, color: "rgba(124,58,237,1)" },
  ];

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
      {cards.map((card) => (
        <Card
          key={card.label}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2.5,
            borderRadius: "16px",
            boxShadow: "none",
            backgroundColor: colors.primary[900],
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              flexShrink: 0,
              borderRadius: "100%",
              backgroundColor: `${card.color.replace(",1)", ",0.15)")}`,
              color: card.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <card.icon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: colors.primary[100] }}>
              {card.value}
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ color: colors.primary[700] }}>
              {card.label}
            </Typography>
          </Box>
        </Card>
      ))}
    </Stack>
  );
};

export default MetricsCards;
